import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash, PencilSimple, Link as LinkIcon, Eye,
  EyeSlash, DotsSixVertical, Image, CursorClick, ShoppingCart, Check
} from '@phosphor-icons/react';

export default function CollectionDetail() {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ name: '', description: '', type: 'custom', is_public: true });
  const [itemForm, setItemForm] = useState({ title: '', description: '', url: '', image_url: '', price: '', offer_id: '' });

  const fetchCollection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/creators/collections/${collectionId}`);
      setCollection(res.data);
      setMetaForm({
        name: res.data.name || '',
        description: res.data.description || '',
        type: res.data.type || 'custom',
        is_public: res.data.is_public !== false,
      });
    } catch (err) {
      toast.error('Collection not found');
      navigate('/creator-studio');
    } finally {
      setLoading(false);
    }
  }, [collectionId, navigate]);

  useEffect(() => { fetchCollection(); }, [fetchCollection]);

  const saveMeta = async () => {
    try {
      await api.put(`/creators/collections/${collectionId}`, metaForm);
      toast.success('Collection updated');
      setEditingMeta(false);
      fetchCollection();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addItem = async () => {
    if (!itemForm.title.trim()) { toast.error('Title is required'); return; }
    if (itemForm.url && !itemForm.url.startsWith('http')) { toast.error('URL must start with http:// or https://'); return; }
    try {
      await api.post(`/creators/collections/${collectionId}/items`, itemForm);
      toast.success('Item added');
      setShowAddItem(false);
      setItemForm({ title: '', description: '', url: '', image_url: '', price: '', offer_id: '' });
      fetchCollection();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add item'); }
  };

  const updateItem = async (itemId) => {
    try {
      await api.put(`/creators/collections/${collectionId}/items/${itemId}`, itemForm);
      toast.success('Item updated');
      setEditingItem(null);
      setItemForm({ title: '', description: '', url: '', image_url: '', price: '', offer_id: '' });
      fetchCollection();
    } catch (err) { toast.error('Failed to update'); }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/creators/collections/${collectionId}/items/${itemId}`);
      toast.success('Item removed');
      fetchCollection();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const startEdit = (item) => {
    setEditingItem(item.item_id);
    setItemForm({ title: item.title, description: item.description || '', url: item.url || '', image_url: item.image_url || '', price: item.price || '', offer_id: item.offer_id || '' });
    setShowAddItem(false);
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading collection...</div>;
  if (!collection) return null;

  const items = collection.items || [];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="collection-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/creator-studio')} className="text-zinc-400 hover:text-white p-2" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          {editingMeta ? (
            <div className="space-y-2">
              <Input data-testid="edit-collection-name" value={metaForm.name} onChange={(e) => setMetaForm(p => ({...p, name: e.target.value}))} className="bg-zinc-800 border-zinc-700 text-lg font-bold" />
              <Input value={metaForm.description} onChange={(e) => setMetaForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700 text-sm" />
              <div className="flex items-center gap-3">
                <select value={metaForm.type} onChange={(e) => setMetaForm(p => ({...p, type: e.target.value}))} className="h-8 bg-zinc-800 border border-zinc-700 rounded-md px-2 text-xs text-zinc-300">
                  <option value="custom">Custom</option>
                  <option value="top_picks">Top Picks</option>
                  <option value="comparison">Comparison</option>
                  <option value="saved_list">Saved List</option>
                </select>
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <input type="checkbox" checked={metaForm.is_public} onChange={(e) => setMetaForm(p => ({...p, is_public: e.target.checked}))} className="rounded bg-zinc-800 border-zinc-700" />
                  Public
                </label>
                <Button size="sm" onClick={saveMeta} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingMeta(false)} className="text-zinc-400 h-7 text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold truncate">{collection.name}</h1>
                <Badge variant="outline" className={`text-[10px] ${collection.is_public ? 'text-emerald-400 border-emerald-700/40' : 'text-zinc-500 border-zinc-700'}`}>
                  {collection.is_public ? 'Public' : 'Private'}
                </Badge>
                <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-700 capitalize">{collection.type?.replace('_', ' ')}</Badge>
                <Button size="sm" variant="ghost" onClick={() => setEditingMeta(true)} className="h-6 px-2 text-zinc-500 hover:text-blue-400" data-testid="edit-meta-btn">
                  <PencilSimple size={14} />
                </Button>
              </div>
              {collection.description && <p className="text-sm text-zinc-500 mt-1">{collection.description}</p>}
              <p className="text-xs text-zinc-600 mt-1">{items.length} items &middot; {collection.views || 0} views</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Item Button */}
      <div className="flex justify-end">
        <Button onClick={() => { setShowAddItem(true); setEditingItem(null); setItemForm({ title: '', description: '', url: '', image_url: '', price: '', offer_id: '' }); }}
          data-testid="add-item-btn" className="bg-emerald-600 hover:bg-emerald-700">
          <Plus size={16} className="mr-2" /> Add Item
        </Button>
      </div>

      {/* Add/Edit Item Form */}
      {(showAddItem || editingItem) && (
        <Card className="bg-zinc-900 border-zinc-700" data-testid="item-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{editingItem ? 'Edit Item' : 'Add New Item'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input data-testid="item-title" value={itemForm.title} onChange={(e) => setItemForm(p => ({...p, title: e.target.value}))} placeholder="Item title (required)" className="bg-zinc-800 border-zinc-700" />
            <Input data-testid="item-url" value={itemForm.url} onChange={(e) => setItemForm(p => ({...p, url: e.target.value}))} placeholder="URL (https://...)" className="bg-zinc-800 border-zinc-700 font-mono text-sm" />
            <Input value={itemForm.description} onChange={(e) => setItemForm(p => ({...p, description: e.target.value}))} placeholder="Description (optional)" className="bg-zinc-800 border-zinc-700" />
            <div className="grid grid-cols-3 gap-3">
              <Input value={itemForm.price} onChange={(e) => setItemForm(p => ({...p, price: e.target.value}))} placeholder="Price (e.g. $29.99)" className="bg-zinc-800 border-zinc-700" />
              <Input value={itemForm.image_url} onChange={(e) => setItemForm(p => ({...p, image_url: e.target.value}))} placeholder="Image URL (optional)" className="bg-zinc-800 border-zinc-700" />
              <Input value={itemForm.offer_id} onChange={(e) => setItemForm(p => ({...p, offer_id: e.target.value}))} placeholder="Offer ID (optional)" className="bg-zinc-800 border-zinc-700" />
            </div>
            <div className="flex gap-2">
              {editingItem ? (
                <Button onClick={() => updateItem(editingItem)} data-testid="update-item-btn" className="bg-blue-600 hover:bg-blue-700">Update Item</Button>
              ) : (
                <Button onClick={addItem} data-testid="save-item-btn" className="bg-emerald-600 hover:bg-emerald-700">Add Item</Button>
              )}
              <Button variant="ghost" onClick={() => { setShowAddItem(false); setEditingItem(null); }} className="text-zinc-400">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items List */}
      {items.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
          <CardContent className="p-10 text-center">
            <ShoppingCart size={36} className="mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-500 font-medium">No items yet</p>
            <p className="text-xs text-zinc-600 mt-1">Add products, affiliate links, or tools to this collection</p>
            <Button onClick={() => setShowAddItem(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Plus size={16} className="mr-2" /> Add Your First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="items-list">
          {items.map((item, idx) => (
            <div key={item.item_id} className="group flex items-start gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-all" data-testid={`item-${item.item_id}`}>
              <div className="text-zinc-700 cursor-grab mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <DotsSixVertical size={16} />
              </div>
              {/* Thumbnail */}
              <div className="w-14 h-14 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 overflow-hidden">
                {item.image_url ? (
                  <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Image size={20} className="text-zinc-600" />
                )}
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-zinc-200 truncate">{item.title}</h3>
                  {item.price && <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-[10px] shrink-0">{item.price}</Badge>}
                </div>
                {item.description && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{item.description}</p>}
                {item.url && (
                  <a href={item.url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1 truncate">
                    <LinkIcon size={10} /> {item.url}
                  </a>
                )}
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-600">
                  <span><CursorClick size={10} className="inline mr-0.5" />{item.clicks || 0} clicks</span>
                  <span><Check size={10} className="inline mr-0.5" />{item.conversions || 0} conv.</span>
                  {item.offer_id && <span className="font-mono">{item.offer_id}</span>}
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" onClick={() => startEdit(item)} className="h-7 px-2 text-blue-400 hover:text-blue-300" data-testid={`edit-item-${item.item_id}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteItem(item.item_id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20" data-testid={`delete-item-${item.item_id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
